import { useParams, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { KBEditor } from "@/components/kb/KBEditor";
import { useArticle, useCreateArticle, useUpdateArticle } from "@/hooks/useKBData";

export function KBEditPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const isEditing = !!slug; // If slug exists, we're editing (route: kb/:slug/edit)

  // Fetch article data if editing
  const { data: articleData, isLoading: articleLoading, error: articleError } = useArticle(
    isEditing ? slug! : ""
  );

  // Mutations
  const createMutation = useCreateArticle();
  const updateMutation = useUpdateArticle();

  const article = articleData?.article;

  const handleSave = async (data: any) => {
    try {
      if (isEditing && slug) {
        // Update existing article
        await updateMutation.mutateAsync({ slug, data });
        navigate(`/app/kb/${slug}`);
      } else {
        // Create new article
        const result = await createMutation.mutateAsync(data);
        navigate(`/app/kb/${result.article.slug}`);
      }
    } catch (error) {
      console.error('Failed to save article:', error);
      throw error; // Let KBEditor handle the error
    }
  };

  const handleCancel = () => {
    if (isEditing && slug) {
      navigate(`/app/kb/${slug}`);
    } else {
      navigate("/app/kb");
    }
  };

  if (isEditing && articleLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (isEditing && (articleError || !article)) {
    return (
      <div className="max-w-4xl mx-auto text-center py-12">
        <h1 className="text-2xl font-bold mb-4">Article Not Found</h1>
        <p className="text-muted-foreground mb-4">
          The article you're trying to edit doesn't exist or has been removed.
        </p>
        <button
          onClick={() => navigate("/app/kb")}
          className="text-primary hover:underline"
        >
          Back to Knowledge Base
        </button>
      </div>
    );
  }

  const isSaving = createMutation.isPending || updateMutation.isPending;

  return (
    <KBEditor
      article={article}
      onSave={handleSave}
      onCancel={handleCancel}
      isSaving={isSaving}
    />
  );
}